import {pricingMatrixIndexOfReference, acreInSF, pmtFunction} from './constants';
import { addCommasToNumber } from './../../../utils';

export default {
    name: 'PRICING RANGE',
    description: '',
    hasNamesColumn: true,
    headerRow: [],
    rows: {
        'bps-change': {
            name: 'BPS change',
            hidden: false,
            valueCells: [{
                unit: '%',
                type: 'number',
                editable: true,
                withDecimals: true,
                value: 0.25
            }]
        },
        'price-psf-change': {
            name: 'Price PSF Change',
            hidden: true,
            valueCells: [{
                unit: '$',
                type: 'number',
                editable: true,
                withDecimals: true,
                value: 5
            }]
        },
        'value': {
            name: 'Price',
            generalGetComputedValue(tables, columnIndex) {
                const leaseType = tables['lease-summary'].rows['lease-type'].value;
                const isVacantLeaseType = leaseType === 'Vacant';
                if (isVacantLeaseType) {
                    const grossLeasableArea = tables['offering-summary'].rows['gross-leasable-area'].value;
                    const pricePSF = parseFloat(tables['pricing-matrix'].rows['price-psf'].valueCells[columnIndex].getComputedValue(tables).internalValue);
                    const value = Math.ceil((grossLeasableArea * pricePSF / 5000)) * 5000;
                    const rawValue = ((grossLeasableArea * pricePSF / 5000)) * 5000
                    const displayValue = value && !isNaN(value) ? `$${value.toLocaleString()}` : '-';
                    return {internalValue: value, displayValue: displayValue, rawValue};
                }
                const netOperatingIncome = tables['offering-summary'].rows['net-operating-income'].getComputedValue(tables).internalValue;
                const capRate = parseFloat(tables['pricing-matrix'].rows['cap-rate'].valueCells[columnIndex].getComputedValue(tables).internalValue);
                const value = capRate ? Math.ceil((netOperatingIncome / (capRate / 100) / 5000)) * 5000 : null;
                const displayValue = value && !isNaN(value) ? `$${value.toLocaleString()}` : '-';
                return {internalValue: value, displayValue: displayValue};
            },
            valueCells: ([...new Array(5)]).map((el, index) => {
                return {
                    unit: '$',
                    type: 'number',
                    editable: false,
                    getComputedValue(tables) {
                        return tables['pricing-matrix'].rows['value'].generalGetComputedValue(tables, index);
                    }
                }
            }),
        },
        'cap-rate': {
            name: 'Cap Rate',
            hidden: false,
            generalGetComputedValue(tables, bpsMultiplier) {
                const baseCapRate = parseFloat(tables['pricing-matrix'].rows['cap-rate'].valueCells[pricingMatrixIndexOfReference].getComputedValue(tables).internalValue);
                const bpsChange = parseFloat(tables['pricing-matrix'].rows['bps-change'].valueCells[0].value);
                const value = baseCapRate + bpsChange * bpsMultiplier;
                const displayValue = value && !isNaN(value) ? `${value.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits :2})}%` : '-';
                return {internalValue: value, displayValue: displayValue};
            },
            valueCells: ([...new Array(5)]).map((el, index) => {
                const cell = {
                    unit: '$',
                    type: 'number',
                    editable: false
                };
                if (index === pricingMatrixIndexOfReference) {
                    cell.getComputedValue = function (tables) {
                        const value = parseFloat(tables['offering-summary'].rows['cap-rate'].value);
                        const displayValue = value && !isNaN(value) ? `${value.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits :2})}%` : '-';
                        return {internalValue: value, displayValue: displayValue};
                    }
                } else {
                    cell.getComputedValue = function (tables) {
                        return tables['pricing-matrix'].rows['cap-rate'].generalGetComputedValue(tables, -1 + index);
                    }
                }
                return cell;
            }),
        },
        'price-psf': {
            name: 'Price PSF',
            hidden: true,
            generalGetComputedValue(tables, pricePsfMultiplier) {
                const isLeaseCheckVacant = tables['lease-summary'].rows['lease-type'].value == "Vacant";
                const basePricePSF = parseFloat(tables['pricing-matrix'].rows['price-psf'].valueCells[pricingMatrixIndexOfReference].getComputedValue(tables).internalValue);
                const pricePsfChange = parseFloat(tables['pricing-matrix'].rows['price-psf-change'].valueCells[0].value);
                const value = isLeaseCheckVacant ? parseFloat(basePricePSF - pricePsfChange * pricePsfMultiplier).toFixed(2) : parseFloat(basePricePSF + pricePsfChange * pricePsfMultiplier).toFixed(2);
                const displayValue = value && !isNaN(value) ? `$${value.toLocaleString()}` : '-';
                return {internalValue: value, displayValue: displayValue};
            },
            valueCells: ([...new Array(5)]).map((el, index) => {
                const cell = {
                    unit: '$',
                    type: 'number',
                    editable: false
                };
                if (index === pricingMatrixIndexOfReference) {
                    cell.getComputedValue = function (tables) {
                        const value = tables['offering-summary'].rows['price-psf'].value;
                        const displayValue = value && !isNaN(value) ? `$${value.toLocaleString()}` : '-';
                        return {internalValue: value, displayValue: displayValue};
                    }
                } else {
                    cell.getComputedValue = function (tables) {
                        return tables['pricing-matrix'].rows['price-psf'].generalGetComputedValue(tables, -1 + index);
                    }
                }
                return cell;
            }),
        },
        'cash-on-cash': {
            name: 'Cash on Cash',
            valueCells: ([...new Array(5)]).map((el, index) => {
                return {
                    unit: '%',
                    type: 'number',
                    editable: false,
                    tooltip: '= cash_flow / down_payment',
                    getComputedValue(tables) {
                        const cashFlow = tables['pricing-matrix'].rows['cash-flow'].valueCells[index].getComputedValue(tables).internalValue;
                        const downPayment = tables['pricing-matrix'].rows['down-payment'].valueCells[index].getComputedValue(tables).internalValue;
                        const value = parseFloat(cashFlow / downPayment * 100).toFixed(2);
                        const displayValue  = value && !isNaN(value) ? `${value.toLocaleString()}%` : '-';
                        return {internalValue: value, displayValue: displayValue};
                    }
                }
            }),
        },
        'sale-price-psf': {
            name: 'Price PSF',
            generalGetComputedValue(tables, columnIndex) {
                const isLeaseCheckVacant = tables['lease-summary'].rows['lease-type'].value == "Vacant";
                const pricingValue = parseFloat(tables['pricing-matrix'].rows['value'].valueCells[columnIndex].getComputedValue(tables).internalValue);
                const grossLeasableArea = parseInt(tables['offering-summary'].rows['gross-leasable-area'].value);
                const value = isLeaseCheckVacant ? parseInt(pricingValue / grossLeasableArea).toFixed(2) : parseFloat(pricingValue / grossLeasableArea).toFixed(2);
                const vacantPriceDisplayValue = tables['pricing-matrix'].rows['value'].valueCells[columnIndex].getComputedValue(tables).rawValue && parseFloat(tables['pricing-matrix'].rows['value'].valueCells[columnIndex].getComputedValue(tables).rawValue);
                const vacantPricePSFValue = Math.ceil(vacantPriceDisplayValue/grossLeasableArea)
                const numericConversionValue = !isLeaseCheckVacant ? +value : vacantPricePSFValue
                const displayValue = numericConversionValue && !isNaN(numericConversionValue) ? `$${numericConversionValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '-';
                return {internalValue: value, displayValue: displayValue};
            },
            valueCells: ([...new Array(5)]).map((el, index) => {
                return {
                    unit: '$',
                    type: 'number',
                    editable: false,
                    tooltip: '= price / gross_leasable_area',
                    getComputedValue(tables) {
                        return tables['pricing-matrix'].rows['sale-price-psf'].generalGetComputedValue(tables, index);
                    }
                }
            }),
        },
        'land-price-psf': {
            name: 'PSF - Land',
            generalGetComputedValue(tables, columnIndex) {
                const pricingValue = parseFloat(tables['pricing-matrix'].rows['value'].valueCells[columnIndex].getComputedValue(tables).internalValue);
                const lotSize = parseFloat(tables['offering-summary'].rows['lot-size'].value);
                const value = parseFloat(pricingValue / (lotSize * acreInSF)).toFixed(2);
                const numbericValue = +value
                const displayValue  = numbericValue && !isNaN(numbericValue) ? `$${numbericValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '-';
                return {internalValue: value, displayValue: displayValue};
            },
            valueCells: ([...new Array(5)]).map((el, index) => {
                return {
                    unit: '$',
                    type: 'number',
                    editable: false,
                    tooltip: '= price / (lot_size * 43560)',
                    getComputedValue(tables) {
                        return tables['pricing-matrix'].rows['land-price-psf'].generalGetComputedValue(tables, index);
                    }
                }
            }),
        },
        'down-payment': {
            name: 'Down Payment',
            generalGetComputedValue(tables, columnIndex) {
                const pricingValue = parseFloat(tables['pricing-matrix'].rows['value'].valueCells[columnIndex].getComputedValue(tables).internalValue);
                const downPaymentPercentage = tables['pricing-matrix-annex'].rows['down-payment'].valueCells[0].value / 100;
                const value = parseInt(pricingValue * downPaymentPercentage);
                const displayValue  = value && !isNaN(value) ? `$${value.toLocaleString()}` : '-';
                return {internalValue: value, displayValue: displayValue};
            },
            valueCells: ([...new Array(5)]).map((el, index) => {
                return {
                    unit: '$',
                    type: 'number',
                    editable: false,
                    tooltip: '= price * down_payment_percentage',
                    getComputedValue(tables) {
                        return tables['pricing-matrix'].rows['down-payment'].generalGetComputedValue(tables, index);
                    }
                }
            }),
        },
        'loan-amount': {
            name: 'Loan Amount',
            generalGetComputedValue(tables, columnIndex) {
                const pricingValue = parseFloat(tables['pricing-matrix'].rows['value'].valueCells[columnIndex].getComputedValue(tables).internalValue);
                const downPayment = parseFloat(tables['pricing-matrix'].rows['down-payment'].valueCells[columnIndex].getComputedValue(tables).internalValue);
                const value = parseInt(pricingValue - downPayment);
                const displayValue  = value && !isNaN(value) ? `$${value.toLocaleString()}` : '-';
                return {internalValue: value, displayValue: displayValue};
            },
            valueCells: ([...new Array(5)]).map((el, index) => {
                return {
                    unit: '$',
                    type: 'number',
                    editable: false,
                    tooltip: '= price - down_payment',
                    getComputedValue(tables) {
                        return tables['pricing-matrix'].rows['loan-amount'].generalGetComputedValue(tables, index);
                    }
                }
            }),
        },
        'loan-payment': {
            name: 'Loan Payment',
            generalGetComputedValue(tables, columnIndex) {
                const interestRate = tables['pricing-matrix-annex'].rows['interest-rate'].valueCells[1].value;
                const amortization = tables['pricing-matrix-annex'].rows['amortization'].valueCells[1].value;
                const loanAmount = tables['pricing-matrix'].rows['loan-amount'].valueCells[columnIndex].getComputedValue(tables).internalValue;
                const value = parseInt(-pmtFunction(interestRate/100/ 12, amortization * 11.8, loanAmount) * 12);
                const displayValue  = value && !isNaN(value) ? `$${value.toLocaleString()}` : '-';
                return {internalValue: value, displayValue: displayValue};
            },
            valueCells: ([...new Array(5)]).map((el, index) => {
                return {
                    unit: '$',
                    type: 'number',
                    editable: false,
                    tooltip: '= -PMT(interest_rate/12, amortization*12, loan_amount) * 12',
                    getComputedValue(tables) {
                        return tables['pricing-matrix'].rows['loan-payment'].generalGetComputedValue(tables, index);
                    }
                }
            }),

        },
        'cash-flow': {
            name: 'Cash Flow',
            generalGetComputedValue(tables, columnIndex) {
                const netOperatingIncome = tables['offering-summary'].rows['net-operating-income'].getComputedValue(tables).internalValue;
                const loanPayment = tables['pricing-matrix'].rows['loan-payment'].valueCells[columnIndex].getComputedValue(tables).internalValue;
                const value = parseInt(netOperatingIncome - loanPayment);
                const displayValue  = value && !isNaN(value) ? `$${value.toLocaleString()}` : '-';
                return {internalValue: value, displayValue: displayValue};
            },
            valueCells: ([...new Array(5)]).map((el, index) => {
                return {
                    unit: '$',
                    type: 'number',
                    editable: false,
                    tooltip: '= net_operating_income - loan_payment',
                    getComputedValue(tables) {
                        return tables['pricing-matrix'].rows['cash-flow'].generalGetComputedValue(tables, index);
                    }
                }
            }),

        },
        'monthly-cash-flow': {
            name: 'Monthly Cash Flow',
            generalGetComputedValue(tables, columnIndex) {
                const loanPayment = tables['pricing-matrix'].rows['cash-flow'].valueCells[columnIndex].getComputedValue(tables).internalValue;
                const value = parseInt(loanPayment / 12);
                const displayValue  = value && !isNaN(value) ? `$${value.toLocaleString()}` : '-';
                return {internalValue: value, displayValue: displayValue};
            },
            valueCells: ([...new Array(5)]).map((el, index) => {
                return {
                    unit: '$',
                    type: 'number',
                    editable: false,
                    tooltip: '= cash_flow / 12',
                    getComputedValue(tables) {
                        return tables['pricing-matrix'].rows['monthly-cash-flow'].generalGetComputedValue(tables, index);
                    }
                }
            }),
        }
    },
};